import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useApiToken } from './useApiToken'
import { customersApi } from '../lib/api/customers'

export const CUSTOMERS_KEY = ['customers'] as const

export function useCustomers() {
  const getToken = useApiToken()
  return useQuery({
    queryKey: CUSTOMERS_KEY,
    queryFn: async () => {
      const token = await getToken()
      return customersApi.list(token)
    },
  })
}

export function useCreateCustomer() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { customer_name: string; tenant_id?: string }) => {
      const token = await getToken()
      return customersApi.create(token, data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY })
      toast.success(`Customer "${vars.customer_name}" created.`)
    },
    onError: (e: Error) => toast.error('Failed to create customer: ' + e.message),
  })
}

export function useRenameCustomer() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ customerId, newName }: { customerId: string; newName: string }) => {
      const token = await getToken()
      return customersApi.rename(token, customerId, newName)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY })
      toast.success(`Customer renamed to "${vars.newName}".`)
    },
    onError: (e: Error) => toast.error('Failed to rename: ' + e.message),
  })
}

export function useDeleteCustomer() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (customerId: string) => {
      const token = await getToken()
      return customersApi.delete(token, customerId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY })
      toast.success('Customer deleted.')
    },
    onError: (e: Error) => toast.error('Failed to delete customer: ' + e.message),
  })
}

export function useRegenerateKey() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (customerId: string) => {
      const token = await getToken()
      return customersApi.regenerateKey(token, customerId)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
    onError: (e: Error) => toast.error('Failed to regenerate key: ' + e.message),
  })
}
